/**
 * ChartInfoSection - Chart Information Section
 * Displays basic chart information: Chart name, Place, Date, Time, Timezone, Altitude
 * Uses EditableField for inline editing of Chart name and Place
 * Also includes Short Links tab for sharing charts
 */

import React, { useState, useEffect } from 'react';
import { JatakaResponse } from '../../../../models/api';
import { AccordionSection } from './AccordionSection';
import { AccordionItem } from './types';
import { SharedChartData, SharedViewMode } from '../../../../types/share.types';
import { useSectionState } from './hooks/useSectionState';
import { globalEventStream } from '../universal_architecture/core/EventStream';
import { Text, HStack, Badge } from '@chakra-ui/react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useChartQuotaAndShares } from '../../../../hooks/useChartQuotaAndShares';
import { BasicInfo, extractChartInfoData, getShowLocationSetting, generateChartInfoItems } from './components/chartInfoUtils';
import { ChartInfoDetail } from './components/ChartInfoDetail';
import { ShortLinksContent } from './components/ShortLinksContent';

export interface ChartInfoSectionProps {
    apiResponse?: JatakaResponse | null;
    className?: string;
    shareMode?: boolean;
    sharedViewMode?: SharedViewMode;
    sharedChartData?: SharedChartData;
    chartId?: string;
}

// Tab type for header switcher
type ActiveTab = 'chart-info' | 'short-links';

/**
 * Main ChartInfoSection component
 */
export const ChartInfoSection: React.FC<ChartInfoSectionProps> = ({
    apiResponse,
    className,
    shareMode = false,
    sharedViewMode = 'author',
    sharedChartData,
    chartId: propChartId
}) => {
    const { isAuthenticated } = useAuth();

    // Active tab state
    const [activeTab, setActiveTab] = useState<ActiveTab>('chart-info');

    // Subscribe to location visibility changes for reactive updates
    const [showLocationSetting, setShowLocationSetting] = useState(getShowLocationSetting);

    // Chart quota and shares via shared hook
    const { chartQuota, backendShares, loadingQuota, handleDeleteShortLink } = useChartQuotaAndShares({
        apiResponse,
        shareMode,
        eventSource: 'chart-info-section',
    });

    useEffect(() => {
        const subscription = globalEventStream.subscribe('settings.location.changed', (event: any) => {
            const newValue = event.data?.showLocation ?? getShowLocationSetting();
            setShowLocationSetting(newValue);
        });
        return () => subscription.unsubscribe();
    }, []);

    // Use prop chartId only - no context dependency to prevent re-renders on tab switch
    const chartId = propChartId || '';

    // Storage key for state persistence
    const storageKey = shareMode && sharedChartData?.short_code
        ? sharedChartData.short_code
        : (chartId || 'default');

    const { state, loadAccordionState, saveAccordionState, isLoading: isStateLoading } = useSectionState(
        storageKey,
        'chart-info'
    );

    // Default sections
    const DEFAULT_SECTIONS = ['chart-info'];

    // Initialize state with defaults
    React.useEffect(() => {
        if (isStateLoading || !storageKey) return;

        if (!state?.hasInteracted) {
            const savedSections = Array.from(loadAccordionState());
            if (savedSections.length === 0) {
                saveAccordionState(new Set(DEFAULT_SECTIONS));
            }
        }
    }, [isStateLoading, state, storageKey, saveAccordionState, loadAccordionState]);

    // Items mapper with shared props
    const itemsMapperWithSharedProps = React.useCallback((data: { basic_info: BasicInfo }) => {
        return generateChartInfoItems(data, sharedViewMode, sharedChartData, showLocationSetting);
    }, [sharedViewMode, sharedChartData, showLocationSetting]);

    // Load expanded sections
    const defaultExpandedSections = React.useMemo(() => {
        if (isStateLoading) return null;

        const savedSections = Array.from(loadAccordionState());
        if (!state?.hasInteracted && savedSections.length === 0) {
            return DEFAULT_SECTIONS;
        }
        return savedSections;
    }, [isStateLoading, loadAccordionState, state]);

    const handleAccordionChange = React.useCallback((expandedSections: Set<string>) => {
        saveAccordionState(expandedSections);
    }, [saveAccordionState]);

    if (!apiResponse) {
        return (
            <div className={className} style={{ padding: '16px', color: '#666' }}>
                No chart info available
            </div>
        );
    }

    if (isStateLoading) {
        return <div className={className}>Loading...</div>;
    }

    // Check if user is authenticated (for showing Short Links tab)
    const showShortLinksTab = !shareMode && isAuthenticated;

    // Custom header with tab switcher
    // isItemExpanded: if true, stop propagation; if false, let click expand the section
    const customHeader = (_item: AccordionItem, isItemExpanded: boolean) => (
        <HStack spacing={0} fontSize="sm" fontWeight="medium">
            <Text
                cursor="pointer"
                opacity={activeTab === 'chart-info' ? 1 : 0.5}
                onClick={(e) => {
                    if (isItemExpanded) e.stopPropagation();
                    setActiveTab('chart-info');
                }}
                _hover={{ opacity: 0.8 }}
                transition="opacity 0.2s"
            >
                Chart Info
            </Text>
            {showShortLinksTab && (
                <>
                    <Text mx={1} opacity={0.5}>/</Text>
                    <Text
                        cursor="pointer"
                        opacity={activeTab === 'short-links' ? 1 : 0.5}
                        onClick={(e) => {
                            if (isItemExpanded) e.stopPropagation();
                            setActiveTab('short-links');
                        }}
                        _hover={{ opacity: 0.8 }}
                        transition="opacity 0.2s"
                    >
                        Short Links
                        {chartQuota && chartQuota.active_count > 0 && (
                            <Badge ml={1} fontSize="2xs" colorScheme="gray" variant="subtle">
                                {chartQuota.active_count}
                            </Badge>
                        )}
                    </Text>
                </>
            )}
        </HStack>
    );

    return (
        <AccordionSection
            dataExtractor={extractChartInfoData}
            itemsMapper={itemsMapperWithSharedProps}
            defaultExpandedSections={defaultExpandedSections || []}
            loadingMessage="Loading Chart Info..."
            idPrefix="chart-info"
            apiResponse={apiResponse}
            className={className}
            onAccordionChange={handleAccordionChange}
            customHeader={customHeader}
            customDetailRenderer={(item: AccordionItem) => {
                if (activeTab === 'short-links' && showShortLinksTab) {
                    return (
                        <ShortLinksContent
                            apiResponse={apiResponse}
                            chartQuota={chartQuota}
                            loadingQuota={loadingQuota}
                            backendShares={backendShares}
                            onDelete={handleDeleteShortLink}
                        />
                    );
                }
                return (
                    <ChartInfoDetail
                        item={item}
                        apiResponse={apiResponse}
                        shareMode={shareMode}
                        chartId={chartId}
                    />
                );
            }}
        />
    );
};

export default ChartInfoSection;
